// A rule in words, and its past: the server's state machine (apps/server/src/alerts/state.ts) run over the counts of the
// last hours, so a rule shows how often it would have fired before it is saved.

export type AlertKind = 'threshold' | 'absence' | 'new_issue' | 'relative';
export type Direction = 'up' | 'down';

export interface RuleShape {
    kind: AlertKind;
    threshold: number;
    windowMinutes: number;
    direction: Direction;
    percent: number;
    compareDays: number;
}

const DAY_MS = 86_400_000;

// A new rule filled in, from wherever the lines are being looked at: EXPLORE's query, an issue's fingerprint.
export interface RuleSeed {
    env?: string;
    q?: string;
    name?: string;
    kind?: AlertKind;
    threshold?: number;
}

export function newRuleLink(seed: RuleSeed): string {
    const params = new URLSearchParams({ new: '1' });
    for (const [key, value] of Object.entries(seed)) {
        if (value !== undefined && value !== '') {
            params.set(key, String(value));
        }
    }

    return `/alerts?${params.toString()}`;
}

const KINDS: readonly AlertKind[] = ['threshold', 'absence', 'new_issue', 'relative'];

export function seedFrom(search: string): RuleSeed | null {
    const params = new URLSearchParams(search);
    if (!params.get('new')) {
        return null;
    }
    const kind = params.get('kind') as AlertKind | null;
    const threshold = Number(params.get('threshold'));

    return {
        ...(params.get('env') ? { env: params.get('env') as string } : {}),
        ...(params.get('q') !== null ? { q: params.get('q') as string } : {}),
        ...(params.get('name') ? { name: (params.get('name') as string).slice(0, 100) } : {}),
        ...(kind && KINDS.includes(kind) ? { kind } : {}),
        ...(params.get('threshold') !== null && Number.isInteger(threshold) && threshold >= 0 ? { threshold } : {}),
    };
}

export const sameTime = (days: number): string =>
    days === 1 ? 'the same time yesterday' : days === 7 ? 'the same time last week' : `the same time ${days} days ago`;

// How much more or fewer: "2×" for whole multiples, "50% more", "75% fewer".
export const byHowMuch = (direction: Direction, percent: number): string =>
    direction === 'down' ? `${percent}% fewer` : percent % 100 === 0 ? `${percent / 100 + 1}×` : `${percent}% more`;

export function describeRule(rule: RuleShape): string {
    const minutes = `${rule.windowMinutes} min`;
    switch (rule.kind) {
        case 'new_issue':
            return 'an error never seen before';
        case 'absence':
            return `no line in ${minutes}`;
        case 'relative': {
            const by = byHowMuch(rule.direction, rule.percent);
            const than = rule.direction === 'up' && rule.percent % 100 === 0 ? `more than ${by}` : `${by} than`;

            const floor = rule.direction === 'up' ? `with ${rule.threshold} lines at least` : `when it had ${rule.threshold} lines at least`;

            return `${than} ${sameTime(rule.compareDays)} in ${minutes}, ${floor}`;
        }
        default:
            return `more than ${rule.threshold} lines in ${minutes}`;
    }
}

// The server's relative condition: more (fewer) than the baseline by the percent, the floor on the larger side, and
// never without a baseline.
export function holdsRelative(direction: Direction, count: number, baseline: number | null, percent: number, floor: number): boolean {
    if (baseline === null) {
        return false;
    }
    const least = Math.max(1, floor);

    return direction === 'up'
        ? count >= least && count > baseline * (1 + percent / 100)
        : baseline >= least && count < baseline * (1 - percent / 100);
}

// Where a relative rule's line sits for a baseline: above it fires (up), below it fires (down).
export const triggerLevel = (direction: Direction, baseline: number, percent: number): number =>
    direction === 'up' ? baseline * (1 + percent / 100) : baseline * (1 - percent / 100);

export interface Point {
    t: number;
    count: number;
    // The same window `compareDays` earlier (relative rules).
    base?: number;
}

// A point's baseline, or null when the environment's first line came after that window started.
export const baselineOf = (point: Point, compareDays: number, firstAt: number | null | undefined): number | null =>
    firstAt !== null && firstAt !== undefined && firstAt <= point.t - compareDays * DAY_MS ? (point.base ?? 0) : null;

export interface Simulation {
    // Where each firing started and ended (ms); an open end is still firing at the last point.
    episodes: Array<{ from: number; to: number | null }>;
    // Which points held the condition.
    holding: boolean[];
}

export function simulate(
    points: readonly Point[],
    rule: Omit<RuleShape, 'kind' | 'windowMinutes'> & { kind: Exclude<AlertKind, 'new_issue'>; confirmChecks: number; recoverChecks: number },
    firstAt?: number | null,
): Simulation {
    const confirm = Math.max(1, rule.confirmChecks);
    const recover = Math.max(1, rule.recoverChecks);
    const episodes: Simulation['episodes'] = [];
    const holding: boolean[] = [];
    let firing = false;
    let streak = 0;
    for (const point of points) {
        const holds =
            rule.kind === 'absence'
                ? point.count === 0
                : rule.kind === 'relative'
                  ? holdsRelative(rule.direction, point.count, baselineOf(point, rule.compareDays, firstAt), rule.percent, rule.threshold)
                  : point.count > rule.threshold;
        holding.push(holds);
        if (firing) {
            streak = holds ? 0 : streak + 1;
            if (streak >= recover) {
                firing = false;
                streak = 0;
                const last = episodes.at(-1);
                if (last) {
                    last.to = point.t;
                }
            }
        } else if (holds) {
            streak += 1;
            if (streak >= confirm) {
                firing = true;
                streak = 0;
                episodes.push({ from: point.t, to: null });
            }
        } else {
            streak = 0;
        }
    }

    return { episodes, holding };
}
